<!--
 * @Author: Nemo
 * @Description: 响应式弹窗组件测试页面
 * @Date: 2024-05-29
-->
<template>
  <div class="responsive-dialog-demo">
    <h1>响应式弹窗组件示例</h1>
    <p>该组件在PC端以dialog形式居中显示，在移动端以popup形式从底部弹出</p>
    <p class="device-info">当前设备类型: {{ isMobile ? '移动端' : 'PC端' }}</p>

    <div class="demo-section">
      <h2>基础用法</h2>
      <el-button type="primary" @click="showBasicDialog = true">打开基础弹窗</el-button>
      <ResponsiveDialog
        :visible.sync="showBasicDialog"
        title="基础弹窗"
        @close="onDialogClose"
        @confirm="onDialogConfirm"
        @cancel="onDialogCancel"
      >
        <p>这是一个基础弹窗的内容区域</p>
        <p>组件会根据当前设备类型自动切换显示方式</p>
      </ResponsiveDialog>
    </div>

    <div class="demo-section">
      <h2>自定义内容</h2>
      <el-button type="success" @click="showCustomDialog = true">打开自定义弹窗</el-button>
      <ResponsiveDialog
        :visible.sync="showCustomDialog"
        title="自定义内容弹窗"
        confirmText="提交"
        cancelText="返回"
      >
        <div class="custom-content">
          <el-form label-width="80px">
            <el-form-item label="姓名">
              <el-input v-model="form.name" placeholder="请输入姓名"></el-input>
            </el-form-item>
            <el-form-item label="邮箱">
              <el-input v-model="form.email" placeholder="请输入邮箱"></el-input>
            </el-form-item>
            <el-form-item label="消息">
              <el-input type="textarea" v-model="form.message" placeholder="请输入消息"></el-input>
            </el-form-item>
          </el-form>
        </div>
      </ResponsiveDialog>
    </div>

    <div class="demo-section">
      <h2>自定义底部</h2>
      <el-button type="warning" @click="showCustomFooterDialog = true">打开自定义底部弹窗</el-button>
      <ResponsiveDialog
        :visible.sync="showCustomFooterDialog"
        title="自定义底部弹窗"
        :showDefaultFooter="false"
      >
        <p>这个弹窗使用了自定义的底部按钮区域</p>
        <template #footer>
          <div class="custom-footer">
            <el-button size="small" @click="showCustomFooterDialog = false">取消</el-button>
            <el-button size="small" type="danger" @click="handleDelete">删除</el-button>
            <el-button size="small" type="primary" @click="handleSave">保存</el-button>
          </div>
        </template>
      </ResponsiveDialog>
    </div>

    <div class="demo-section">
      <h2>长内容弹窗</h2>
      <el-button type="danger" @click="showLongContentDialog = true">打开长内容弹窗</el-button>
      <ResponsiveDialog
        :visible.sync="showLongContentDialog"
        title="长内容弹窗"
      >
        <div class="long-content">
          <p v-for="n in 20" :key="n">这是长内容弹窗的第 {{ n }} 行内容。当内容超出最大高度时会出现滚动条。</p>
        </div>
      </ResponsiveDialog>
    </div>

    <div class="demo-section">
      <h2>账单信息表单</h2>
      <el-button type="info" @click="showBillingFormDialog = true">打开账单表单弹窗</el-button>
      <ResponsiveDialog
        :visible.sync="showBillingFormDialog"
        title="账单信息表单"
        :billing-fields="billingFields"
        :show-billing-form="true"
        @confirm="handleBillingFormSubmit"
        @field-change="handleFieldChange"
      >
        <div class="payment-content">
          <p>请确认以下账单信息是否正确，如需修改请在表单中编辑。</p>
          <div v-if="submittedBillingData" class="submitted-data">
            <p>提交的表单数据：</p>
            <pre>{{ JSON.stringify(submittedBillingData, null, 2) }}</pre>
          </div>
        </div>
      </ResponsiveDialog>
    </div>

    <div class="demo-section">
      <h2>Apple Pay弹窗</h2>
      <el-button type="primary" @click="showApplePayDialog = true">打开Apple Pay弹窗</el-button>
      <ResponsiveDialog
        :visible.sync="showApplePayDialog"
        type="apple"
      >
        <div class="payment-content">
          <p>这是一个Apple Pay弹窗示例，展示了如何使用type="apple"属性。</p>
          <p>组件会自动显示Apple Pay图标和标题。</p>
        </div>
      </ResponsiveDialog>
    </div>

    <div class="demo-section">
      <h2>Google Pay弹窗</h2>
      <el-button type="success" @click="showGooglePayDialog = true">打开Google Pay弹窗</el-button>
      <ResponsiveDialog
        :visible.sync="showGooglePayDialog"
        type="google"
      >
        <div class="payment-content">
          <p>这是一个Google Pay弹窗示例，展示了如何使用type="google"属性。</p>
          <p>组件会自动显示Google Pay图标和标题。</p>
        </div>
      </ResponsiveDialog>
    </div>
  </div>
</template>

<script>
import ResponsiveDialog from '@/views/aggregate/components/responsive-dialog/index.vue';

export default {
  name: 'ResponsiveDialogDemo',
  components: {
    ResponsiveDialog,
  },
  data() {
    return {
      isMobile: false,
      showBasicDialog: false,
      showCustomDialog: false,
      showCustomFooterDialog: false,
      showLongContentDialog: false,
      showApplePayDialog: false,
      showGooglePayDialog: false,
      showBillingFormDialog: false,
      submittedBillingData: null,
      form: {
        name: '',
        email: '',
        message: '',
      },
      billingFields: [
        {
          name: 'address',
          require: 'Y',
          value: 'Unit 1113, 11/F, Tower 2, Cheung Sha Wan Plaza, 833 Cheung Sha Wan Road, Lai Chi Kok',
          display: 'Y',
        },
        {
          name: 'city',
          require: 'Y',
          value: 'HK',
          display: 'Y',
        },
        {
          name: 'country',
          require: 'Y',
          value: 'US',
          display: 'Y',
        },
        {
          name: 'email',
          require: 'Y',
          value: 'test@test.com',
          display: 'Y',
        },
        {
          name: 'firstName',
          require: 'Y',
          value: 'test',
          display: 'Y',
        },
        {
          name: 'lastName',
          require: 'Y',
          value: 'test',
          display: 'Y',
        },
        {
          name: 'number',
          require: 'Y',
          value: '',
          display: 'Y',
        },
        {
          name: 'phone',
          require: 'Y',
          value: '18600000000',
          display: 'Y',
        },
        {
          name: 'postalCode',
          require: 'Y',
          value: '430000',
          display: 'Y',
        },
        {
          name: 'province',
          require: 'Y',
          value: 'HK',
          display: 'Y',
        },
        {
          name: 'street',
          require: 'Y',
          value: '',
          display: 'Y',
        },
      ],
    };
  },
  mounted() {
    this.checkDevice();
    window.addEventListener('resize', this.checkDevice);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkDevice);
  },
  methods: {
    checkDevice() {
      const screenWidth = window.innerWidth;
      this.isMobile = screenWidth <= 768 || /Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent);
    },
    onDialogClose() {
    },
    onDialogConfirm() {
      this.showBasicDialog = false;
    },
    onDialogCancel() {
    },
    handleDelete() {
      this.showCustomFooterDialog = false;
    },
    handleSave() {
      this.$message.success('保存成功');
      this.showCustomFooterDialog = false;
    },
    handleBillingFormSubmit(formData) {
      this.submittedBillingData = formData;
      this.$message.success('账单信息已提交');
      // 不关闭弹窗，展示提交后的数据
    },
    handleFieldChange(fieldData) {
    },
  },
};
</script>

<style lang="scss" scoped>
.responsive-dialog-demo {
  padding: 20px;
  max-width: 800px;
  margin: 0 auto;

  h1 {
    margin-bottom: 20px;
    color: #182058;
  }

  .device-info {
    margin-bottom: 30px;
    font-weight: bold;
    color: #182058;
  }

  .demo-section {
    margin-bottom: 30px;
    padding: 20px;
    border-radius: 8px;
    background-color: #f8f9fa;

    h2 {
      margin-bottom: 15px;
      color: #182058;
    }
  }

  .custom-content {
    padding: 10px;
  }

  .custom-footer {
    display: flex;
    justify-content: flex-end;

    .el-button {
      margin-left: 10px;
    }
  }

  .long-content {
    p {
      margin-bottom: 10px;
    }
  }

  .payment-content {
    padding: 10px;

    .submitted-data {
      margin-top: 16px;
      padding: 12px;
      background-color: #f0f9ff;
      border-radius: 4px;
      border-left: 3px solid #182058;

      pre {
        margin: 0;
        white-space: pre-wrap;
        word-break: break-all;
        font-size: 13px;
        color: #333;
      }
    }
  }
}

// 为移动端添加响应式样式
@media (max-width: 768px) {
  .responsive-dialog-demo {
    padding: 15px;

    h1 {
      font-size: 20px;
    }

    .demo-section {
      padding: 15px;

      h2 {
        font-size: 16px;
      }
    }
  }
}
</style>
